import {
  ArrowUpIcon,
  PersonGroupIcon,
  ChatIcon,
  HandHeartIcon,
  ExclamationmarkTriangleIcon,
  RocketIcon,
  ShieldCheckmarkIcon,
} from '@navikt/aksel-icons';
import { useTranslation } from 'react-i18next';
import classes from './TeamExpectations.module.css';

interface TeamExpectationsProps {
  commitments?: Array<{ statement: string }>;
  expectations?: Array<{ statement: string }>;
  maxItems?: number;
  showFooter?: boolean;
}

export const TeamExpectations = ({
  commitments,
  expectations,
  maxItems = 4,
  showFooter = true,
}: TeamExpectationsProps) => {
  const { t } = useTranslation();

  // Default hardcoded items if no data provided (backwards compatibility)
  const defaultCommitments = [
    { statement: t('publicSurvey.teamExpectations.commitments.item1') },
    { statement: t('publicSurvey.teamExpectations.commitments.item2') },
    { statement: t('publicSurvey.teamExpectations.commitments.item3') },
    { statement: t('publicSurvey.teamExpectations.commitments.item4') },
  ];

  const defaultExpectations = [
    { statement: t('publicSurvey.teamExpectations.expectations.item1') },
    { statement: t('publicSurvey.teamExpectations.expectations.item2') },
    { statement: t('publicSurvey.teamExpectations.expectations.item3') },
  ];

  const displayCommitments = commitments || defaultCommitments;
  const displayExpectations = expectations || defaultExpectations;

  const limitedCommitments = displayCommitments.slice(0, maxItems);
  const limitedExpectations = displayExpectations.slice(0, maxItems);

  return (
    <div className={classes.container}>
      <div className={classes.grid}>
        {/* Card 1 - Team Commitments */}
        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <div className={classes.iconWrapper} data-accent="green">
              <ArrowUpIcon fontSize="1.25rem" />
            </div>
            <div className={classes.headerContent}>
              <h3 className={classes.cardTitle}>
                {t('publicSurvey.teamExpectations.commitments.title')}
              </h3>
              <p className={classes.cardSubtitle}>
                {t('publicSurvey.teamExpectations.commitments.subtitle')}
              </p>
            </div>
          </div>

          <div className={classes.itemList}>
            {limitedCommitments.map((commitment, index) => {
              const icons = [ChatIcon, HandHeartIcon, ExclamationmarkTriangleIcon, RocketIcon];
              const Icon = icons[index % icons.length];
              return (
                <div key={index} className={classes.item}>
                  <div className={classes.itemIcon} data-accent="green">
                    <Icon fontSize="1.125rem" />
                  </div>
                  <span className={classes.itemText}>
                    {commitment.statement}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Card 2 - Team Expectations */}
        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <div className={classes.iconWrapper} data-accent="purple">
              <PersonGroupIcon fontSize="1.25rem" />
            </div>
            <div className={classes.headerContent}>
              <h3 className={classes.cardTitle}>
                {t('publicSurvey.teamExpectations.expectations.title')}
              </h3>
              <p className={classes.cardSubtitle}>
                {t('publicSurvey.teamExpectations.expectations.subtitle')}
              </p>
            </div>
          </div>

          <div className={classes.itemList}>
            {limitedExpectations.map((expectation, index) => {
              const icons = [ChatIcon, HandHeartIcon, ShieldCheckmarkIcon];
              const Icon = icons[index % icons.length];
              return (
                <div key={index} className={classes.item}>
                  <div className={classes.itemIcon} data-accent="purple">
                    <Icon fontSize="1.125rem" />
                  </div>
                  <span className={classes.itemText}>
                    {expectation.statement}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showFooter && (
        <p className={classes.footer}>
          {t('publicSurvey.teamExpectations.footer')}
        </p>
      )}
    </div>
  );
};
